import type { SearchResults } from '@horizon/shared';
import type {
  ReviewData,
  ReviewLog,
  ReviewReminder,
  Statistics,
  WeeklyProgress,
} from '@horizon/shared';
import type {
  Goal,
  GoalSnapshot,
  Project,
  Task,
  CreateTaskInput,
  SavedFilter,
  TaskFilter,
} from '@horizon/shared';

/** Ошибка API в форме, пригодной для показа пользователю. */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const CSRF_COOKIE = 'horizon_csrf';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Токен double-submit: сервер кладёт его в куку, доступную скрипту, а клиент
 * возвращает в заголовке. Чужой сайт куку прочитать не может, поэтому и
 * заголовок подделать не сможет (docs/SECURITY.md, раздел 9).
 */
function csrfToken(): string | null {
  const match = new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`).exec(document.cookie);
  return match?.[1] ?? null;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET';
  const token = SAFE_METHODS.has(method) ? null : csrfToken();

  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(token ? { 'x-csrf-token': token } : {}),
        ...init?.headers,
      },
      // Кросс-доменных запросов у приложения не бывает: клиент и API
      // отдаются с одного origin.
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError('offline', 'network', 0);
  }

  if (res.status === 204) return undefined as T;

  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const error = (body as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(error?.code ?? 'unknown', error?.message ?? 'Ошибка', res.status);
  }
  return body as T;
}

export interface Me {
  timeZone: string;
  weekStartsOn: number;
  dailyLoadMinutes: number;
  locale: string;
  today: string;
}

export interface AuthState {
  authenticated: boolean;
  secondFactorPending: boolean;
}

export interface SessionInfo {
  id: string;
  current: boolean;
  userAgent: string | null;
  ipAddress: string | null;
  createdAt: string;
  lastSeenAt: string;
}

export interface AuthEvent {
  kind: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export const api = {
  search: (q: string) => request<SearchResults>(`/search?q=${encodeURIComponent(q)}`),
  task: async (id: string) => (await request<{ task: Task }>(`/tasks/${id}`)).task,
  onboarding: () => request<{ step: number }>('/onboarding'),
  advanceOnboarding: (input: { step: number; title?: string; skip?: boolean }) =>
    request<{ step: number }>('/onboarding', { method: 'POST', body: JSON.stringify(input) }),
  buildInfo: () => request<{ version: string; revision: string }>('/build-info'),
  review: (week?: string) =>
    request<ReviewData>(`/reviews${week ? `?weekStart=${encodeURIComponent(week)}` : ''}`),
  startReview: (weekStart: string) =>
    request<{ review: ReviewLog }>('/reviews', {
      method: 'POST',
      body: JSON.stringify({ weekStart }),
    }),
  saveReview: ({
    week,
    ...input
  }: {
    week: string;
    version: number;
    step: number;
    note: string;
    finished: boolean;
  }) =>
    request<{ review: ReviewLog }>(`/reviews/${week}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  reviewAction: ({
    week,
    ...input
  }: {
    week: string;
    version: number;
    ids: string[];
    action: 'defer' | 'drop';
    scheduledFor?: string;
  }) =>
    request<{ review: ReviewLog }>(`/reviews/${week}/actions`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  reminder: () => request<ReviewReminder>('/review-reminder'),
  setReminder: (day: number | null) =>
    request<ReviewReminder>('/review-reminder', { method: 'PATCH', body: JSON.stringify({ day }) }),
  statistics: () => request<Statistics>('/statistics'),
  weeklyProgress: (id: string) =>
    request<{ snapshots: WeeklyProgress[] }>(`/goals/${id}/weekly-progress`).then(
      (r) => r.snapshots,
    ),

  authState: () => request<AuthState>('/auth/state'),
  login: (email: string, password: string) =>
    request<{ secondFactor: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  secondFactor: (code: string) =>
    request<{ authenticated: boolean }>('/auth/second-factor', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  logout: () => request<{ authenticated: boolean }>('/auth/logout', { method: 'POST' }),
  listSessions: () =>
    request<{ sessions: SessionInfo[] }>('/auth/sessions').then((r) => r.sessions),
  revokeSession: (id: string) => request<void>(`/auth/sessions/${id}`, { method: 'DELETE' }),
  revokeOtherSessions: () =>
    request<{ revoked: number }>('/auth/sessions/revoke-others', { method: 'POST' }),
  listAuthEvents: () => request<{ events: AuthEvent[] }>('/auth/events').then((r) => r.events),

  me: () => request<Me>('/me'),
  listFilters: () => request<{ filters: SavedFilter[] }>('/task-filters').then((r) => r.filters),
  saveFilter: (input: { name: string; filter: TaskFilter }) =>
    request('/task-filters', { method: 'POST', body: JSON.stringify(input) }),
  deleteFilter: (id: string) => request(`/task-filters/${id}`, { method: 'DELETE' }),
  bulkTasks: (input: {
    ids: string[];
    patch: { scheduledFor?: string | null; projectId?: string | null };
  }) => request('/tasks/bulk', { method: 'POST', body: JSON.stringify(input) }),
  listTasks: () => request<{ tasks: Task[] }>('/tasks').then((r) => r.tasks),
  createTask: (input: Partial<CreateTaskInput> & { title: string }) =>
    request<{ task: Task }>('/tasks', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.task),
  quickAdd: (input: string, parse: boolean, projectId?: string | null) =>
    request<{ task: Task }>('/tasks/quick-add', {
      method: 'POST',
      body: JSON.stringify({ input, parse, projectId }),
    }).then((r) => r.task),
  updateTask: (id: string, patch: Record<string, unknown>) =>
    request<{ task: Task }>(`/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }).then((r) => r.task),
  deleteTask: (id: string) => request<void>(`/tasks/${id}`, { method: 'DELETE' }),
  deferTasks: (ids: string[], scheduledFor: string) =>
    request<{ moved: string[] }>('/tasks/defer', {
      method: 'POST',
      body: JSON.stringify({ ids, scheduledFor }),
    }).then((r) => r.moved),

  listGoals: (archived = false) =>
    request<{ goals: Goal[] }>(`/goals${archived ? '?archived=true' : ''}`).then((r) => r.goals),
  getGoal: (id: string) =>
    request<{ goal: Goal; projects: Project[]; snapshots: GoalSnapshot[] }>(`/goals/${id}`),
  createGoal: (input: Record<string, unknown>) =>
    request<{ goal: Goal }>('/goals', { method: 'POST', body: JSON.stringify(input) }).then(
      (r) => r.goal,
    ),
  updateGoal: (id: string, patch: Record<string, unknown>) =>
    request<{ goal: Goal }>(`/goals/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }).then(
      (r) => r.goal,
    ),
  archiveGoal: (id: string, archived: boolean) =>
    request<{ goal: Goal }>(`/goals/${id}/archive`, {
      method: 'POST',
      body: JSON.stringify({ archived }),
    }).then((r) => r.goal),

  listProjects: (archived = false) =>
    request<{ projects: Project[] }>(`/projects${archived ? '?archived=true' : ''}`).then(
      (r) => r.projects,
    ),
  getProject: (id: string) => request<{ project: Project; goal: Goal | null }>(`/projects/${id}`),
  createProject: (input: Record<string, unknown>) =>
    request<{ project: Project }>('/projects', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.project),
  updateProject: (id: string, patch: Record<string, unknown>) =>
    request<{ project: Project }>(`/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }).then((r) => r.project),
  archiveProject: (id: string, archived: boolean) =>
    request<{ project: Project }>(`/projects/${id}/archive`, {
      method: 'POST',
      body: JSON.stringify({ archived }),
    }).then((r) => r.project),
};
